import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useMemberOrchestrator } from "@nju/data-access-portal-member";
import { useMemberProducts } from "@nju/data-access-portal-product";
import { VerificationData } from "@nju/dev-tip";
import { getFeatureFlagValue } from "@nju/feature-flags";
import { FAQButton } from "@nju/feature-portal-help";
import { ErrorMessage, Input } from "@nju/form";
import { portalRoutes } from "@nju/routes";
import {
  A,
  Back,
  Button,
  GeneralErrorNotification,
  Panel,
  Separator,
  Text,
} from "@nju/ui";
import { ISelectedSimCardType } from "../../types";
import { changeSimEsimDataVerificationMachine } from "./changeSimEsimDataVerification.machine";
import { ChangeSimEsimDataVerificationWrongData } from "./ChangeSimEsimDateVerifiactionWrongData";

interface IChangeSimEsimDataVerificationPageProps {
  selectedSimCardType: ISelectedSimCardType;
}

export function ChangeSimEsimDataVerificationPage({
  selectedSimCardType,
}: IChangeSimEsimDataVerificationPageProps) {
  const router = useRouter();
  const products = useMemberProducts();
  const { mobile } = useMemberOrchestrator();
  const params =
    portalRoutes.mojeKonto.mojaKarta.wymianaKarty.esim.potwierdzDane
      .input(router.query)
      .unwrap();
  const isDevTipFlagEnabled = getFeatureFlagValue("devTip") === "1";

  const [current, send] = useMachine(changeSimEsimDataVerificationMachine, {
    context: {
      userId: params?.userId,
      selectedSimCardType,
      products,
    },
    actions: {
      redirectToChangeSimSummaryPage: () => {
        if (selectedSimCardType === "eSIM") {
          router.push(
            portalRoutes.mojeKonto.mojaKarta.wymianaKarty.esim.podsumowanie.get(
              {
                ...(params?.userId && { userId: params.userId }),
                mode: "mobile",
              },
            ),
          );
        }

        if (selectedSimCardType === "SIM") {
          router.push(
            portalRoutes.mojeKonto.mojaKarta.wymianaKarty.sim.podsumowanie.get({
              ...(params?.userId && { userId: params.userId }),
              mode: "mobile",
            }),
          );
        }
      },
    },
  });

  const { context, matches } = current;
  const { dataCode, errorCode } = context;

  const refresh = React.useCallback(() => {
    router.reload();
  }, [router]);

  const onInputChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      ({ target }) => {
        const { value } = target;
        send({
          type: "SET_CODE",
          value,
        });
      },
      [send],
    );

  const handleSubmit: React.FormEventHandler<HTMLFormElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();
        send({ type: "SUBMIT" });
      },
      [send],
    );

  if (matches("wrongData")) {
    return <ChangeSimEsimDataVerificationWrongData refresh={refresh} />;
  }

  if (matches("error")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          return window.location.reload();
        }}
      />
    );
  }

  return (
    <Panel>
      <Back
        iconColor="accent"
        noPadding={true}
        text="wstecz"
        textTone="accent"
        type="LINK"
        url={portalRoutes.mojeKonto.mojaKarta.wymianaKarty.index.get({
          ...(params?.userId && { userId: params.userId }),
        })}
      />
      <Separator size="xs" tone="invisible" />
      <Text
        size={{ "@onlyMobile": "keyS", "@fromTablet": "keyM" }}
        weight="bold"
      >
        potwierdź Twoje dane
      </Text>
      <Separator size="xs" tone="invisible" />
      <Text size={{ "@onlyMobile": "s", "@fromTablet": "m" }}>
        Dla celów bezpieczeństwa podaj 4 ostatnie cyfry numeru PESEL lub 4 znaki
        dokumentu, który został użyty do potwierdzenia Twojej tożsamości.
      </Text>
      <Separator size="xs" tone="invisible" />
      <form onSubmit={handleSubmit}>
        <div className="from-tablet:max-w-[400px] pb-[4px]">
          <Input
            data-cy="pesel-form"
            label="4 ostatnie znaki"
            maxLength={4}
            name="data-code"
            onChange={onInputChange}
            value={dataCode}
          />
        </div>
        {errorCode && <ErrorMessage message={errorCode} withIcon={false} />}
        <Separator size="s" tone="invisible" />
        <FAQButton
          categoryId="NJU_WWW_FORGOTTEN_DOC"
          title="nie pamiętasz, jaki to był dokument?"
        />
        <Separator size="s" tone="invisible" />
        <div className="flex justify-between only-mobile:flex-col-reverse only-mobile:gap-3">
          <Link
            href={portalRoutes.mojeKonto.mojaKarta.index.get({
              ...(params?.userId && { userId: params.userId }),
            })}
            passHref={true}
          >
            <A
              size={{
                "@onlyMobile": "s",
              }}
              variant="secondary"
            >
              anuluj
            </A>
          </Link>
          <Button
            isLoading={!matches("init")}
            size={{
              "@onlyMobile": "s",
            }}
            type="submit"
          >
            dalej
          </Button>
        </div>
        {isDevTipFlagEnabled && (
          <VerificationData userId={mobile?.adminId ?? mobile?.userId} />
        )}
      </form>
    </Panel>
  );
}
